// @givmo/design-system — IconButton (SaaS layer).
//
// A square icon-only control (`ghost` / `secondary` / `danger`) for toolbars, table rows, and top
// bars. Matches the SaaS button radius (`rounded-md`) so it never reads as a lozenge next to a
// labelled button. `danger` is the destructive affordance (red hover tint + danger focus ring).
// `aria-label` is required (icon-only). When used as a Base UI `render` target the glyph is injected,
// so `children` is optional.
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { FOCUS_RING } from '../lib/focus';
import { testIdProp, type TestIdProps } from '../lib/testid';

const iconButtonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center rounded-md outline-none',
    'transition-[background-color,box-shadow,border-color,color]',
    // Focus = tinted ring AND a 2px solid outline — the house pairing, published as one constant so
    // a fourth copy cannot take only the ring half (which is what happened; see `lib/focus.ts`).
    FOCUS_RING,
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      size: {
        sm: 'size-32',
        md: 'size-36',
      },
      tone: {
        ghost: 'text-icon hover:bg-surface-hover hover:text-text-secondary active:bg-neutral-100',
        secondary:
          'border border-border-strong bg-surface text-icon shadow-xs hover:border-neutral-400 hover:bg-surface-hover active:bg-neutral-150',
        // destructive affordance — red hover tint + danger focus ring/outline (danger-600 =
        // 4.83:1 on white, 3.92:1 against its own ring tint).
        danger:
          'text-icon hover:bg-danger-50 hover:text-danger-600 active:bg-danger-100 focus-visible:shadow-ring-danger focus-visible:outline-danger-600',
      },
    },
    defaultVariants: { size: 'md', tone: 'ghost' },
  },
);

export interface IconButtonProps
  extends
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof iconButtonVariants>,
    TestIdProps {
  /** The glyph. Optional so the button can be used as a Base UI `render` target (children injected). */
  children?: ReactNode;
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, children, tone, size, testId, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(iconButtonVariants({ tone, size }), className)}
      {...testIdProp(testId)}
      {...props}
    >
      {children}
    </button>
  );
});

export { iconButtonVariants };
